import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as moment from 'moment';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { BusinessReportsService } from './services/business-reports.service';
import { TimelinessScoringMode, TIMELINESS_SCORING_OPTIONS } from './tabs/timeliness-tab/timeliness-tab.component';
import { exportBusinessReport } from './utils/business-reports-export.util';
import { getTabLabel, TAB_IDS } from './utils/tabs';

@Component({
  selector: 'app-business-reports',
  templateUrl: './business-reports.component.html'
})
export class BusinessReportsComponent implements OnInit {
  readonly TAB_IDS = TAB_IDS;
  readonly scoringModeOptions = TIMELINESS_SCORING_OPTIONS;

  activeTab: string;
  dates = this.buildDefaultDateRange();
  scoringMode: TimelinessScoringMode = 'merged';

  timeZone: string;
  pickerValue: any[] = [];

  loading = false;
  /** Soft refresh (e.g. scoring mode) — keep cards visible while values update. */
  refreshing = false;
  error = '';
  data: any = null;

  private activeRequest: Subscription;

  get canDownload(): boolean {
    return !this.loading && !this.refreshing && !!this.data && !this.error;
  }

  /** Timeliness only — block future dates; other tabs allow any future range. */
  get pickerMaxDate(): any {
    if (this.activeTab !== TAB_IDS.TIMELINESS || !this.timeZone) {
      return null;
    }
    return moment.tz(this.timeZone);
  }

  constructor(
    private route: ActivatedRoute,
    private sharedDataService: SharedDataService,
    private businessReportsService: BusinessReportsService
  ) { }

  ngOnInit() {
    this.activeTab = this.route.snapshot.data.tab || TAB_IDS.OVERVIEW;

    this.sharedDataService
      .getUser()
      .pipe(take(1))
      .subscribe((user) => {
        this.timeZone = user && user.timeZone;
        this.dates = this.businessReportsService.savedRange || this.buildDefaultDateRange(this.timeZone);
        this.syncPickerFromDates();

        this.route.data.subscribe((data) => {
          this.activeTab = data.tab || TAB_IDS.OVERVIEW;
          this.applyTimelinessDateConstraints();
          this.loadActiveTab();
        });
      });
  }

  get pageTitle(): string {
    return getTabLabel(this.activeTab);
  }

  onPickerChange(event: any[]) {
    if (!event || !event[0] || !event[1]) return;

    this.dates = {
      startDate: moment(event[0]).tz(this.timeZone).startOf('day').toISOString(),
      endDate: moment(event[1]).tz(this.timeZone).endOf('day').toISOString()
    };
    this.businessReportsService.savedRange = this.dates;
    this.loadActiveTab();
  }

  onScoringModeSelect(event: Event) {
    const value = (event.target as HTMLSelectElement).value as TimelinessScoringMode;
    if (value === this.scoringMode) return;
    this.scoringMode = value;
    // Keep cards mounted; only swap KPI / chart / table values.
    this.loadActiveTab({ soft: true });
  }

  onDownloadReport() {
    if (!this.canDownload) return;

    const { startDate, endDate } = this.dates;
    exportBusinessReport({
      tabId: this.activeTab,
      data: this.data,
      startDate,
      endDate,
      timeZone: this.timeZone,
      scoringMode: this.scoringMode
    });
  }

  private fetcherFor(tabId: string): Observable<any> {
    const { startDate, endDate } = this.dates;
    switch (tabId) {
      case TAB_IDS.OVERVIEW: return this.businessReportsService.fetchOverview(startDate, endDate);
      case TAB_IDS.BREAKDOWN: return this.businessReportsService.fetchBreakdown(startDate, endDate);
      case TAB_IDS.DRIVER_DIGEST: return this.businessReportsService.fetchDriverDigest(startDate, endDate);
      case TAB_IDS.TIMELINESS: return this.businessReportsService.fetchTimeliness(startDate, endDate, this.scoringMode);
      case TAB_IDS.TRIPS: return this.businessReportsService.fetchTrips(startDate, endDate);
      case TAB_IDS.REVENUE: return this.businessReportsService.fetchRevenue(startDate, endDate);
      default: return null;
    }
  }

  /** end -> today (in the given time zone), start -> 6 days before, both snapped to day boundaries to match onPickerChange. */
  private buildDefaultDateRange(timeZone?: string) {
    const end = timeZone ? moment.tz(timeZone) : moment();
    const start = end.clone().subtract(6, 'days');

    return {
      startDate: start.startOf('day').toISOString(),
      endDate: end.endOf('day').toISOString()
    };
  }

  private loadActiveTab(options: { soft?: boolean } = {}) {
    const fetch$ = this.fetcherFor(this.activeTab);
    if (!fetch$) return;

    if (this.activeRequest) {
      this.activeRequest.unsubscribe();
      this.activeRequest = null;
    }

    const soft = !!(options.soft && this.data);
    this.error = '';
    this.refreshing = soft;
    if (!soft) {
      this.loading = true;
    }

    this.activeRequest = fetch$.subscribe({
      next: (data) => {
        this.data = data;
        this.loading = false;
        this.refreshing = false;
        this.activeRequest = null;
      },
      error: (err) => {
        if (!soft) {
          this.data = null;
        }
        this.loading = false;
        this.refreshing = false;
        this.activeRequest = null;
        this.error = (err && err.message) || 'Request failed';
      }
    });
  }

  private syncPickerFromDates() {
    if (!this.timeZone) return;
    this.pickerValue = [
      moment(this.dates.startDate).tz(this.timeZone),
      moment(this.dates.endDate).tz(this.timeZone)
    ];
  }

  /** Clamp saved range to today when opening Timeliness with a future range selected elsewhere. */
  private applyTimelinessDateConstraints() {
    if (this.activeTab !== TAB_IDS.TIMELINESS || !this.timeZone) return;

    const todayEnd = moment.tz(this.timeZone).endOf('day');
    let start = moment(this.dates.startDate).tz(this.timeZone);
    let end = moment(this.dates.endDate).tz(this.timeZone);

    if (end.isAfter(todayEnd)) {
      end = todayEnd.clone();
    }
    if (start.isAfter(todayEnd)) {
      start = todayEnd.clone().startOf('day');
    }

    const nextDates = {
      startDate: start.startOf('day').toISOString(),
      endDate: end.endOf('day').toISOString()
    };

    if (nextDates.startDate !== this.dates.startDate || nextDates.endDate !== this.dates.endDate) {
      this.dates = nextDates;
      this.businessReportsService.savedRange = this.dates;
      this.syncPickerFromDates();
    }
  }
}
